import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@mlm/ui';
import { listBranchLots, type StockLot } from '../../lib/api';
import { ExpiryBadge } from './expiry-badge';
import { costLabel, lotName } from './lot-bits';
import { LotWriteOffDialog } from './lot-write-off-dialog';

/**
 * One product's stock lots at one branch: what its on-hand quantity is made of,
 * each lot with its cost and expiry, and a way to write off a specific lot.
 */
export function LotsSheet({
  branchId,
  product,
  onOpenChange,
}: {
  branchId: string;
  /** The product to show; null closes the sheet. */
  product: { productId: string; name: string; sku: string; qty: number; unit: string } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [writeOff, setWriteOff] = useState<StockLot | null>(null);

  const lots = useQuery({
    queryKey: ['admin-stock-lots', branchId, product?.productId, includeEmpty],
    queryFn: () => listBranchLots(branchId, { productId: product!.productId, includeEmpty }),
    enabled: !!product,
  });

  const rows = lots.data?.lots ?? [];
  const lotted = rows.reduce((sum, l) => sum + l.qtyRemaining, 0);
  const hasVariants = rows.some((l) => l.variantName);
  // Stock from before lots were switched on belongs to no lot. Only meaningful
  // for a product without variations: `product.qty` is the product-level count.
  const unlotted = product && !hasVariants ? Math.max(product.qty - lotted, 0) : 0;

  return (
    <>
      <Sheet open={!!product} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Stock lots{product ? ` · ${product.name}` : ''}</SheetTitle>
            <SheetDescription>
              {product ? `SKU ${product.sku}. Oldest stock goes out first.` : ''}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={includeEmpty}
                onCheckedChange={(v) => setIncludeEmpty(v === true)}
              />
              Show used-up lots
            </label>

            {lots.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No lots yet. A lot is created each time this product is received.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Lot</th>
                      <th className="px-3 py-2 font-medium">Received</th>
                      <th className="px-3 py-2 font-medium">Expiry</th>
                      <th className="px-3 py-2 text-right font-medium">Unit cost</th>
                      <th className="px-3 py-2 text-right font-medium">Left</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((l) => (
                      <tr
                        key={l.id}
                        className={`border-b last:border-0 ${l.qtyRemaining === 0 ? 'text-muted-foreground' : ''}`}
                      >
                        <td className="px-3 py-2">
                          <span className="font-mono text-xs">{lotName(l)}</span>
                          {l.variantName && (
                            <div className="text-xs text-muted-foreground">{l.variantName}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{l.receivedAt.slice(0, 10)}</td>
                        <td className="px-3 py-2">
                          {l.expiresOn && <div className="tabular-nums">{l.expiresOn}</div>}
                          <ExpiryBadge bucket={l.bucket} daysLeft={l.daysLeft} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {costLabel(l.unitCost)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {l.qtyRemaining.toLocaleString()}
                          <span className="text-xs text-muted-foreground">
                            {' '}
                            / {l.qtyReceived.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {l.qtyRemaining > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => setWriteOff(l)}
                            >
                              Write off
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {unlotted > 0 && (
              <p className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                {unlotted.toLocaleString()} {product?.unit} on hand belong to no lot (stock from
                before lots were switched on). They go out first, at the product's usual cost.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <LotWriteOffDialog
        lot={writeOff}
        onOpenChange={(open) => {
          if (!open) setWriteOff(null);
        }}
      />
    </>
  );
}
